import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import {
  NarrativeThread,
  NarrativeThreadsFile,
  Scene,
  ScenesFile,
  sortScenes,
  sortThreads,
  threadColor,
  threadVariant,
  type ThreadVariant,
  tokenizeSceneRefs,
} from '@lib/data/story';
import { useDebouncedValue } from '@lib/hooks/debounce';
import { useAutoScrollToActive } from '@lib/hooks/useAutoScrollToActive';
import { formatClockLabel, formatDurationToString } from '@lib/utils/units';
import { videoPlayerStore, VideoPlayerStore } from '@stores/VideoPlayer';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { CSSProperties, Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react';
import css from './ScenesPanel.module.scss';
import { ThreadLanes } from './ThreadLanes';

type Props = {
  /** `undefined` while loading; `null` when the media has no `scenes.json`. */
  scenes: ScenesFile | null | undefined;
  /** Supplementary — the panel is a scene list first, so a missing threads file just
   *  hides the lanes rather than blocking anything. */
  threads: NarrativeThreadsFile | null | undefined;
  /** Set when `scenes.json` could not be read. NOT the same as the media having no scenes. */
  error?: Error;
  /** Full media duration, so the last scene's missing `end` can be repaired. */
  durationSeconds: number;
  /** Absolute seconds into the media — the panel addresses the whole media, not the moment. */
  onSeek: (seconds: number) => void;
  /**
   * Adopts a span as the marked range, in absolute seconds. Omit it and the panel stays
   * seek-only, which is what a host with nothing to hand a range to should do.
   *
   * Offered because a scene is a range someone already worked out: dragging one out by hand on
   * an 8px scrub bar is a few seconds of fiddling for a worse answer than the boundaries the
   * pipeline detected.
   */
  onSelectRange?: (start: number, end: number) => void;
  store?: VideoPlayerStore;
};

/**
 * The media's structure: what is playing now, where each narrative thread runs, and every scene
 * as a jumpable row.
 *
 * The layout is one bounded detail slot over a uniform list, and both halves of that matter. An
 * earlier version expanded the playing row in place, which moved the list under the reader
 * every time playback crossed a boundary, and stacked thread detail on top of scene detail,
 * which left the list a sliver on a laptop. One slot showing one subject — the selected thread,
 * or failing that the playing scene — keeps the chrome a fixed height and the list a list.
 *
 * Scene boundaries are not drawn here: the player's own scrub bar carries them (see
 * `ChapterMark`), which is where someone scrubbing is already looking.
 */
export const ScenesPanel = observer(
  ({
    scenes,
    threads,
    error,
    durationSeconds,
    onSeek,
    onSelectRange,
    store = videoPlayerStore,
  }: Props) => {
    const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null);
    /**
     * Which subject's summary is expanded, as `thread-3` / `scene-7`.
     *
     * Keyed by subject rather than held as a boolean so it cannot outlive what it described:
     * the slot's subject changes on its own as playback moves, and a stale `true` would open
     * the next scene's summary for a reader who never asked.
     */
    const [expandedFor, setExpandedFor] = useState<string | null>(null);
    const { activeRef, scrollRef } = useAutoScrollToActive<HTMLButtonElement>();
    const debouncedCurrentTime = useDebouncedValue(store.currentTime, 50);

    const sceneList = useMemo(
      () => sortScenes(scenes?.scenes ?? [], durationSeconds),
      [scenes, durationSeconds],
    );
    const threadList = useMemo(() => sortThreads(threads?.threads ?? []), [threads]);

    // Colour and secondary channel come out of one pass over one index, so the two halves of a
    // thread's identity cannot drift apart. See `threadVariant` for why there are two.
    const { colorByThreadId, variantByThreadId } = useMemo(() => {
      const colors = new Map<number, string>();
      const variants = new Map<number, ThreadVariant>();
      threadList.forEach((thread, i) => {
        colors.set(thread.thread_id, threadColor(i));
        variants.set(thread.thread_id, threadVariant(i));
      });
      return { colorByThreadId: colors, variantByThreadId: variants };
    }, [threadList]);
    const threadsBySceneId = useMemo(() => {
      const map = new Map<number, NarrativeThread[]>();
      for (const thread of threadList) {
        for (const sceneId of thread.scenes_included ?? []) {
          const existing = map.get(sceneId);
          if (existing) {
            existing.push(thread);
          } else {
            map.set(sceneId, [thread]);
          }
        }
      }
      return map;
    }, [threadList]);
    const sceneById = useMemo(
      () => new Map(sceneList.map((scene) => [scene.scene_id, scene])),
      [sceneList],
    );

    // Resolved from the list rather than trusted: the modal stays mounted across a change of
    // media on the library page, so a selection can outlive the threads it named.
    const selectedThread = threadList.find((t) => t.thread_id === selectedThreadId) ?? null;

    if (error) {
      // Not "no scenes" — the file may exist and have failed to load, and saying otherwise
      // would state a fact about the media we don't have.
      return <span className={css.placeholder}>Failed to load scenes</span>;
    }
    if (scenes === undefined) {
      return <span className={css.placeholder}>Loading...</span>;
    }
    if (sceneList.length === 0) {
      return <span className={css.placeholder}>No scenes for this media</span>;
    }

    // Scenes tile the media as `[start, end)`, so a playhead exactly on a boundary belongs to
    // the scene that is starting.
    const activeScene =
      sceneList.find(
        (scene) =>
          (scene.start ?? 0) <= debouncedCurrentTime && (scene.end ?? 0) > debouncedCurrentTime,
      ) ?? null;
    const activeSceneId = activeScene?.scene_id ?? null;
    const activeThreads = activeSceneId === null ? [] : (threadsBySceneId.get(activeSceneId) ?? []);
    const activeThreadIds = new Set(activeThreads.map((thread) => thread.thread_id));

    // Read off `scenes_included` rather than inverted back through `threadsBySceneId`: it is
    // the thread's own account of itself.
    const memberSceneIds = selectedThread ? new Set(selectedThread.scenes_included ?? []) : null;
    const visibleScenes = memberSceneIds
      ? sceneList.filter((scene) => memberSceneIds.has(scene.scene_id))
      : sceneList;

    /**
     * Threads sharing at least one scene with the selected one, busiest crossing first.
     *
     * This is the only place the threads connect to each other: the data gives them no direct
     * links, and a shared scene is exactly what a crossing is.
     */
    const crossings = selectedThread
      ? threadList
          .filter((thread) => thread.thread_id !== selectedThread.thread_id)
          .map((thread) => ({
            thread,
            shared: (thread.scenes_included ?? []).filter((id) => memberSceneIds!.has(id)).length,
          }))
          .filter((crossing) => crossing.shared > 0)
          .sort((a, b) => b.shared - a.shared)
      : [];

    const lanesDuration =
      durationSeconds > 0
        ? durationSeconds
        : sceneList.reduce((max, scene) => Math.max(max, scene.end ?? 0), 0);

    const jumpToScene = (sceneId: number) => {
      const scene = sceneById.get(sceneId);
      if (scene) {
        onSeek(scene.start ?? 0);
      }
    };
    const toggleThread = (threadId: number) =>
      setSelectedThreadId(selectedThreadId === threadId ? null : threadId);

    const sceneTitle = (scene: Scene) => scene.title ?? `Scene ${scene.scene_id}`;
    const threadTitle = (thread: NarrativeThread) => thread.title ?? `Thread ${thread.thread_id}`;

    // One slot, one subject: the thread you picked, or failing that the scene playing now.
    const subjectKey = selectedThread
      ? `thread-${selectedThread.thread_id}`
      : activeScene
        ? `scene-${activeScene.scene_id}`
        : null;
    const isExpanded = subjectKey !== null && expandedFor === subjectKey;

    const summaryBlock = (text: string) => (
      <ClampedSummary
        text={text}
        expanded={isExpanded}
        onToggle={() => setExpandedFor(isExpanded ? null : subjectKey)}
        sceneById={sceneById}
        onPick={jumpToScene}
      />
    );

    return (
      <div className={css.panel}>
        {/* Detail, lanes and legend are bounded as ONE group — see `.chrome`. */}
        <div className={css.chrome}>
          <div className={css.detail}>
            {selectedThread ? (
              <>
                <div className={css.detailHead}>
                  <span
                    className={css.detailSwatch}
                    style={{ background: colorByThreadId.get(selectedThread.thread_id) }}
                  />
                  <span className={css.detailKicker}>Narrative thread</span>
                  <button
                    type="button"
                    className={css.detailClear}
                    onClick={() => setSelectedThreadId(null)}
                  >
                    Clear
                  </button>
                </div>
                <div className={css.detailTitle}>{threadTitle(selectedThread)}</div>
                {selectedThread.summary && summaryBlock(selectedThread.summary)}
                {crossings.length > 0 && (
                  <GradientScrollDiv
                    className={css.chipRow}
                    innerDivClassName={css.chipRowInner}
                    horizontalScrolling
                    horizontalArrows
                  >
                    <span className={css.chipRowLabel}>Crosses</span>
                    {crossings.map(({ thread, shared }) => (
                      <button
                        key={thread.thread_id}
                        type="button"
                        className={css.miniChip}
                        style={
                          {
                            '--thread-color': colorByThreadId.get(thread.thread_id),
                          } as CSSProperties
                        }
                        title={`Shares ${shared} ${shared === 1 ? 'scene' : 'scenes'} with this thread`}
                        onClick={() => setSelectedThreadId(thread.thread_id)}
                      >
                        <span className={css.miniChipSwatch} />
                        <span className={css.miniChipTitle}>{threadTitle(thread)}</span>
                        <span className={css.miniChipCount}>{shared}</span>
                      </button>
                    ))}
                  </GradientScrollDiv>
                )}
              </>
            ) : activeScene ? (
              <>
                <div className={css.detailHead}>
                  <span className={css.detailKicker}>Now playing</span>
                  <span className={css.detailMeta}>
                    {formatClockLabel(activeScene.start ?? 0)} –{' '}
                    {formatClockLabel(activeScene.end ?? activeScene.start ?? 0)} ·{' '}
                    {formatDurationToString(
                      Math.max(0, (activeScene.end ?? 0) - (activeScene.start ?? 0)),
                    )}
                  </span>
                </div>
                <div className={css.detailTitle}>{sceneTitle(activeScene)}</div>
                {/* In the detail slot rather than on every row: the rows are `<button>`s, so a
                    per-row control would nest a button inside one, and this slot already states
                    the exact range the control would adopt. A scene with no detected end has
                    nothing to select, so the control is absent rather than inert — there is no
                    user action that would make it appear. */}
                {onSelectRange && activeScene.end !== undefined && (
                  <button
                    type="button"
                    className={css.selectRange}
                    onClick={() => onSelectRange(activeScene.start ?? 0, activeScene.end!)}
                  >
                    Select this scene
                  </button>
                )}
                {activeScene.summary && summaryBlock(activeScene.summary)}
                {activeThreads.length > 0 && (
                  <GradientScrollDiv
                    className={css.chipRow}
                    innerDivClassName={css.chipRowInner}
                    horizontalScrolling
                    horizontalArrows
                  >
                    <span className={css.chipRowLabel}>In</span>
                    {activeThreads.map((thread) => (
                      <button
                        key={thread.thread_id}
                        type="button"
                        className={css.miniChip}
                        style={
                          {
                            '--thread-color': colorByThreadId.get(thread.thread_id),
                          } as CSSProperties
                        }
                        title={threadTitle(thread)}
                        onClick={() => toggleThread(thread.thread_id)}
                      >
                        <span className={css.miniChipSwatch} />
                        <span className={css.miniChipTitle}>{threadTitle(thread)}</span>
                      </button>
                    ))}
                  </GradientScrollDiv>
                )}
              </>
            ) : (
              // Before the first scene, or past the last: say what is here rather than leaving an
              // empty slot that reads as something having failed.
              <div className={css.detailIdle}>
                {sceneList.length} scenes
                {threadList.length > 0 &&
                  ` · ${threadList.length} narrative ${threadList.length === 1 ? 'thread' : 'threads'}`}
              </div>
            )}
          </div>

          {lanesDuration > 0 && threadList.length > 0 && (
            <ThreadLanes
              scenes={sceneList}
              threads={threadList}
              colorByThreadId={colorByThreadId}
              variantByThreadId={variantByThreadId}
              durationSeconds={lanesDuration}
              selectedThreadId={selectedThread?.thread_id ?? null}
              onSelectThread={setSelectedThreadId}
              store={store}
            />
          )}

          {threadList.length > 0 && (
            <GradientScrollDiv
              className={css.threadRail}
              innerDivClassName={css.threadRailInner}
              horizontalScrolling
              horizontalArrows
            >
              {threadList.map((thread) => {
                const selected = selectedThread?.thread_id === thread.thread_id;
                return (
                  <button
                    key={thread.thread_id}
                    type="button"
                    className={classNames(css.threadChip, {
                      [css.threadChipOn]: selected,
                      // "Running right now" — the live link from the playhead back to the
                      // threads, so the rail reads as a state of the media, not a menu.
                      [css.threadChipLive]: activeThreadIds.has(thread.thread_id),
                    })}
                    style={
                      { '--thread-color': colorByThreadId.get(thread.thread_id) } as CSSProperties
                    }
                    aria-pressed={selected}
                    title={threadTitle(thread)}
                    onClick={() => toggleThread(thread.thread_id)}
                  >
                    <span className={css.threadChipSwatch} />
                    <span className={css.threadChipTitle}>{threadTitle(thread)}</span>
                    <span className={css.threadChipCount}>
                      {thread.scenes_included?.length ?? 0}
                    </span>
                  </button>
                );
              })}
            </GradientScrollDiv>
          )}

          {selectedThread && (
            <div className={css.filterLine}>
              Showing {visibleScenes.length} of {sceneList.length} scenes
            </div>
          )}
        </div>

        <GradientScrollDiv className={css.list} innerDivClassName={css.listInner} ref={scrollRef}>
          {visibleScenes.map((scene) => {
            const isActive = scene.scene_id === activeSceneId;
            const sceneThreads = threadsBySceneId.get(scene.scene_id) ?? [];
            const start = scene.start ?? 0;
            return (
              // Uniform height, every row. The summary that used to sit here showed about
              // fifteen words of a hundred-word paragraph, usually cut mid-clause; the title is
              // what the list is scanned by. The whole summary is one click away in the slot
              // above — which this click also fills, because seeking makes this the playing
              // scene.
              <button
                key={scene.scene_id}
                type="button"
                ref={isActive ? activeRef : null}
                className={classNames(css.scene, { [css.sceneActive]: isActive })}
                aria-current={isActive || undefined}
                onClick={() => onSeek(start)}
              >
                <span className={css.sceneTime}>{formatClockLabel(start)}</span>
                <span className={css.sceneTitle}>{sceneTitle(scene)}</span>
                {sceneThreads.length > 0 && (
                  <span className={css.sceneDots}>
                    {sceneThreads.map((thread) => (
                      <span
                        key={thread.thread_id}
                        className={css.sceneDot}
                        style={{ background: colorByThreadId.get(thread.thread_id) }}
                        title={threadTitle(thread)}
                      />
                    ))}
                  </span>
                )}
              </button>
            );
          })}
        </GradientScrollDiv>
      </div>
    );
  },
);

/**
 * A summary clamped to three lines, with a toggle that appears exactly when there is something
 * behind the clamp.
 *
 * The toggle is driven by measuring the clamped paragraph rather than by counting characters:
 * how much fits in three lines depends on the rendered width and the words, and this panel is
 * a desktop rail at one end and a phone sheet at the other. A character threshold is wrong at
 * both ends at once — too low and the control opens nothing, too high and text is cut with no
 * way to reach it.
 *
 * Measured only while clamped, since the expanded paragraph is full height and never overflows,
 * and the control stays mounted once expanded so "Show less" remains reachable.
 *
 * The fragment root is deliberate: the paragraph and the toggle are laid out by `.detail`'s
 * flex column (the toggle's `align-self` needs it), so wrapping them in an element of our own
 * would take them out of it.
 */
const ClampedSummary = ({
  text,
  expanded,
  onToggle,
  sceneById,
  onPick,
}: {
  text: string;
  expanded: boolean;
  onToggle: () => void;
  sceneById: Map<number, Scene>;
  onPick: (sceneId: number) => void;
}) => {
  const summaryRef = useRef<HTMLParagraphElement>(null);
  const [overflows, setOverflows] = useState(false);

  // `useLayoutEffect`, not `useEffect`: the first measurement has to land before paint, or the
  // control flickers in a frame after the text it belongs to.
  useLayoutEffect(() => {
    const node = summaryRef.current;
    if (!node || expanded) return;
    const measure = () => setOverflows(node.scrollHeight > node.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [text, expanded]);

  return (
    <>
      <p
        ref={summaryRef}
        className={classNames(css.detailSummary, { [css.detailSummaryClamped]: !expanded })}
      >
        <SceneRefs text={text} sceneById={sceneById} onPick={onPick} />
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          className={css.summaryToggle}
          aria-expanded={expanded}
          onClick={onToggle}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </>
  );
};

/**
 * Thread summaries carry inline `[scene_N]` references. They render as chips that jump the
 * player to that scene — but only when the file actually lists the scene, so a reference to a
 * scene that isn't there stays inert text instead of a control that does nothing.
 */
const SceneRefs = ({
  text,
  sceneById,
  onPick,
}: {
  text: string;
  sceneById: Map<number, Scene>;
  onPick: (sceneId: number) => void;
}) => (
  <>
    {tokenizeSceneRefs(text).map((token, i) => {
      if (token.type === 'text') {
        return <Fragment key={i}>{token.text}</Fragment>;
      }
      const scene = sceneById.get(token.sceneId);
      if (!scene) {
        return <Fragment key={i}>{`[scene_${token.sceneId}]`}</Fragment>;
      }
      return (
        <button
          key={i}
          type="button"
          className={css.sceneRef}
          title={scene.title ?? `Scene ${token.sceneId}`}
          onClick={() => onPick(token.sceneId)}
        >
          №{token.sceneId}
        </button>
      );
    })}
  </>
);
